import { getCartErrorMessage } from '@theme/cart-feedback';

/**
 * <s-quick-add> — Adds one variant to the cart from a product card, then opens the cart drawer.
 *
 * Cards on collection and search pages sit inside the filter <form>, and forms cannot nest,
 * so this is a command-driven element rather than a product form.
 *
 * Required markup:
 *   <s-quick-add id="…" data-variant-id="…" data-quantity="1" data-product-id="…" data-error-message="…">
 *     <button type="button" commandfor="(same id)" command="--add">…</button>
 *     <p data-quick-add-error hidden></p>
 *   </s-quick-add>
 */
class SQuickAdd extends HTMLElement {
  #ac;
  #requestController;

  connectedCallback() {
    this.#ac = new AbortController();
    this.addEventListener('command', (event) => {
      if (event.command !== '--add') return;
      this.#add(event.source);
    }, { signal: this.#ac.signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#requestController?.abort();
  }

  async #add(button) {
    if (this.getAttribute('aria-busy') === 'true') return;
    const variantId = this.dataset.variantId;
    if (!variantId) return;

    // Without Standard Actions there is no on-page cart API; fall back to the product page.
    if (!window.Shopify?.actions?.updateCart) {
      if (this.dataset.productUrl) window.location.assign(this.dataset.productUrl);
      return;
    }

    const quantity = Number.parseInt(this.dataset.quantity || '1', 10) || 1;
    this.#requestController = new AbortController();
    const { signal } = this.#requestController;
    this.#setBusy(true, button);
    this.#showError('');

    try {
      const result = await window.Shopify.actions.updateCart(
        { lines: [{ merchandiseId: variantId, quantity }] },
        { event: { context: 'collection', detail: { source: 's-quick-add', productId: this.dataset.productId } }, signal }
      );
      if (signal.aborted) return;

      const message = result?.userErrors?.[0]?.message || '';
      if (message) {
        this.#showError(message);
      } else {
        await window.Shopify.actions.openCart?.();
      }
    } catch (error) {
      if (signal.aborted) return;
      this.#showError(getCartErrorMessage(error) || this.dataset.errorMessage || '');
    } finally {
      if (!signal.aborted) this.#setBusy(false, button);
    }
  }

  #setBusy(busy, button) {
    if (busy) {
      this.setAttribute('aria-busy', 'true');
      button?.setAttribute('aria-disabled', 'true');
      button?.classList.add('opacity-50', 'cursor-wait');
    } else {
      this.removeAttribute('aria-busy');
      button?.removeAttribute('aria-disabled');
      button?.classList.remove('opacity-50', 'cursor-wait');
    }
  }

  #showError(message) {
    const error = this.querySelector('[data-quick-add-error]');
    if (!error) return;
    error.textContent = message || '';
    error.hidden = !message;
  }
}

if (!customElements.get('s-quick-add')) {
  customElements.define('s-quick-add', SQuickAdd);
}
